import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
export type LegalDocType = 'privacy' | 'terms';
interface LegalModalProps {
  open: boolean;
  doc: LegalDocType;
  onClose: () => void;
}
const content = {
  privacy: {
    title: 'Política de Privacidad',
    updated: 'Última actualización: Mayo 2026',
    sections: [{
      h: '1. Información que Recopilamos',
      p: 'Al adquirir El Método del 1%, recopilamos únicamente la información necesaria para procesar tu compra y entregarte el producto: tu nombre, dirección de correo electrónico y los datos de pago procesados de forma segura por nuestro proveedor externo (Stripe). No almacenamos los datos completos de tu tarjeta en nuestros servidores.'
    }, {
      h: '2. Cómo Usamos tu Información',
      p: 'Utilizamos tus datos exclusivamente para: (a) entregarte el acceso al e-book, (b) enviarte correos relacionados con tu compra, (c) ofrecerte soporte si lo solicitas, y (d) enviarte comunicaciones de bienestar opcionales, de las cuales puedes darte de baja en cualquier momento.'
    }, {
      h: '3. Compartir con Terceros',
      p: 'No vendemos, alquilamos ni compartimos tu información personal con terceros para fines comerciales. Solo compartimos datos con proveedores estrictamente necesarios para operar el servicio (procesador de pagos y servicio de correo).'
    }, {
      h: '4. Cookies',
      p: 'Utilizamos cookies básicas para mejorar tu experiencia en el sitio y entender cómo se usa la página. Puedes desactivarlas desde la configuración de tu navegador.'
    }, {
      h: '5. Tus Derechos',
      p: 'Tienes derecho a acceder, corregir o solicitar la eliminación de tus datos personales. Para ejercer estos derechos, escríbenos al correo de soporte disponible en tu recibo de compra.'
    }, {
      h: '6. Seguridad',
      p: 'Implementamos medidas técnicas razonables para proteger tu información. Sin embargo, ningún sistema en internet es 100% seguro, por lo que no podemos garantizar seguridad absoluta.'
    }]
  },
  terms: {
    title: 'Términos de Servicio',
    updated: 'Última actualización: Mayo 2026',
    sections: [{
      h: '1. Naturaleza del Producto',
      p: 'El Método del 1% es un producto digital (e-book descargable) de contenido informativo y educativo sobre bienestar hormonal femenino. Al completar tu compra, obtienes una licencia personal, no transferible y no exclusiva para uso individual.'
    }, {
      h: '2. Aviso de Salud Importante',
      p: 'El contenido tiene fines exclusivamente educativos e informativos. NO constituye asesoramiento, diagnóstico ni tratamiento médico. No reemplaza la consulta con un profesional de salud calificado. Siempre consulta a tu médico antes de hacer cambios en tu dieta, suplementación, ejercicio o estilo de vida, especialmente si tienes alguna condición médica, estás embarazada o lactando.'
    }, {
      h: '3. Resultados Individuales',
      p: 'Los resultados varían según cada persona. Los testimonios presentados son experiencias individuales y no constituyen una promesa o garantía de resultados específicos. Tu progreso depende de factores personales como salud previa, consistencia y circunstancias individuales.'
    }, {
      h: '4. Política de Compra',
      p: 'Debido a la naturaleza digital del producto, una vez completada la descarga o el acceso al contenido, la compra se considera entregada en su totalidad. Por esta razón, todas las ventas son finales. Te invitamos a revisar la descripción del producto antes de comprar y a contactar nuestro soporte si tienes preguntas.'
    }, {
      h: '5. Propiedad Intelectual',
      p: 'Todo el contenido del e-book —texto, imágenes, diseño y método— está protegido por derechos de autor. No está permitido copiar, redistribuir, revender ni compartir el material con terceros sin autorización por escrito.'
    }, {
      h: '6. Limitación de Responsabilidad',
      p: 'El Cambio Hormonal y sus creadoras no se hacen responsables por decisiones tomadas con base en el contenido del e-book ni por consecuencias derivadas de su aplicación. El uso del material es bajo tu propia responsabilidad.'
    }, {
      h: '7. Modificaciones',
      p: 'Nos reservamos el derecho de actualizar estos términos en cualquier momento. La versión más reciente siempre estará disponible en esta página.'
    }]
  }
};
export function LegalModal({
  open,
  doc,
  onClose
}: LegalModalProps) {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);
  const data = content[doc];
  return <AnimatePresence data-id="element-95">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8" data-id="element-96">
          <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={onClose} data-id="element-97" />

          <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.98
      }} transition={{
        duration: 0.4,
        ease: [0.21, 0.47, 0.32, 0.98]
      }} className="relative bg-cream max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-soft-lg flex flex-col" data-id="element-98">
            <div className="flex items-start justify-between px-8 md:px-12 pt-10 pb-6 border-b border-ink/5" data-id="element-99">
              <div data-id="element-100">
                <h2 className="font-serif text-3xl md:text-4xl text-ink mb-2" data-id="element-101">
                  {data.title}
                </h2>
                <p className="text-[10px] uppercase tracking-[0.2em] text-ink-light" data-id="element-102">
                  {data.updated}
                </p>
              </div>
              <button onClick={onClose} aria-label="Cerrar" className="text-ink-light hover:text-ink transition-colors p-1 -mr-1" data-id="element-103">
                <X className="w-5 h-5" data-id="element-104" />
              </button>
            </div>

            <div className="overflow-y-auto px-8 md:px-12 py-8 space-y-8" data-id="element-105">
              {data.sections.map((s, i) => <div key={i} data-id="element-106">
                  <h3 className="font-serif text-xl text-ink mb-3" data-id="element-107">{s.h}</h3>
                  <p className="text-sm text-ink-light font-light leading-relaxed" data-id="element-108">
                    {s.p}
                  </p>
                </div>)}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}